// ─── RAF Singleton ───────────────────────────────────────────────────────────

const root = typeof window === 'undefined' ? globalThis : window;
const _observers = [];
const _afterObservers = [];
const _beforeObservers = [];
let _rafHandler = null;
let _lastDate = null;
let _once = false;
let _complex = false;
let time = 0;
let dt = 0;


function _frame(now) {
    if (_lastDate === null) _lastDate = now;
    dt = now - _lastDate;
    time += dt;
    _lastDate = now;
    _rafHandler = _once ? null : root.requestAnimationFrame(_frame);
    if (_once) _once = false;
    let i;
    if (_complex) {
        for (i = 0; i < _beforeObservers.length; i++) _beforeObservers[i](dt);
        for (i = 0; i < _observers.length; i++) _observers[i](dt);
        for (i = 0; i < _afterObservers.length; i++) _afterObservers[i](dt);
    } else {
        for (i = 0; i < _observers.length; i++) _observers[i](dt);
    }
}


function _swapRunner() {
    _complex = !!(_afterObservers.length > 0 || _beforeObservers.length > 0);
}


function _addObserver(arr, fn, prepend) {
    if (!fn || !arr) return false;
    if (~arr.indexOf(fn)) return false;
    prepend ? arr.unshift(fn) : arr.push(fn);
    return true;
}


function _removeObserver(arr, fn) {
    if (!fn) return false;
    const index = arr.indexOf(fn);
    if (!~index) return false;
    arr.splice(index, 1);
    return arr.length === 0;
}


function addBefore(fn, prepend) { _addObserver(_beforeObservers, fn, prepend) && _swapRunner(); }
function addAfter(fn, prepend) { _addObserver(_afterObservers, fn, prepend) && _swapRunner(); }
function removeBefore(fn) { _removeObserver(_beforeObservers, fn) && _swapRunner(); }
function removeAfter(fn) { _removeObserver(_afterObservers, fn) && _swapRunner(); }

function add(fn, prepend) { _addObserver(_observers, fn, prepend) && start(); }
function remove(fn) { _removeObserver(_observers, fn) && stop(); }


function start(instant) {
    _once = false;
    if (_rafHandler) return;
    _lastDate = null;
    if (instant) _frame(performance.now());
    else _rafHandler = root.requestAnimationFrame(_frame);
}


function requestOnce() {
    if (_rafHandler) return;
    _once = true;
    _lastDate = null;
    _rafHandler = root.requestAnimationFrame(_frame);
}


function stop() {
    if (!_rafHandler) return;
    root.cancelAnimationFrame(_rafHandler);
    _rafHandler = null;
}


function dispose() {
    stop();
    _observers.length = 0;
    _afterObservers.length = 0;
    _beforeObservers.length = 0;
    _complex = false;
    _lastDate = null;
    time = 0;
    dt = 0;
}


export const raf = {
    add, addAfter, addBefore,
    remove, removeAfter, removeBefore,
    start, stop, requestOnce, dispose,
    get time() { return time },
    get dt() { return dt }
};


// ─── RafTimer ────────────────────────────────────────────────────────────────

class RafTimer {
    constructor(delay, cb, opts = {}) {
        const autostart = opts.autostart !== undefined ? opts.autostart : true;
        this._standalone = opts.standalone !== undefined ? opts.standalone : true;
        this._selfdestruct = opts.selfdestruct !== undefined ? opts.selfdestruct : true;
        this._stopped = true;
        this._remainder = 0;
        this._delay = delay | 0;
        this._remainingTime = delay;
        this._callback = cb === undefined ? function() {} : cb;

        const self = this;
        const _update = this.update;
        const _restart = this.restart;
        this.update = function(dt) { _update.call(self, dt); };
        this.restart = function(n, u) { _restart.call(self, n, u); };

        if (autostart) this.start();
        if (this._delay === 0) this.stop();
    }


    setCallback(newCallback, newDelay) {
        this._callback = newCallback === undefined ? function() {} : newCallback;
        if (newDelay) this.restart(newDelay);
    }


    stop() {
        this._stopped = true;
        if (this._standalone) raf.remove(this.update);
    }


    start() {
        if (!this._stopped) return;
        this.restart();
    }


    restart(newDelay, useRemainder = true) {
        if (newDelay !== undefined) this._delay = newDelay;
        if (this._standalone && this._stopped) raf.add(this.update);
        this._stopped = false;
        this._remainingTime = this._delay - (this._remainder * (+useRemainder));
    }


    update(dt) {
        if (this._stopped) return;
        this._remainingTime -= dt;
        if (this._remainingTime <= 0) {
            this._stopped = true;
            this._remainder = (-this._remainingTime) % this._delay;
            this._callback(this.restart);
            if (this._stopped && this._selfdestruct) this.dispose();
        } else {
            this._remainder = 0;
        }
    }


    dispose() {
        if (this._standalone) raf.remove(this.update);
        this._callback = this.restart = null;
        this._stopped = true;
        this._remainder = 0;
        this._remainingTime = this._delay;
    }
}


export function raftimer(delay, cb, opts) {
    return new RafTimer(delay, cb, opts);
}


// ─── FPS Limiter ─────────────────────────────────────────────────────────────

export function fpsLimiter(framerate, callback) {
    if (callback === undefined) throw new Error('fpsLimiter: callback is required');
    if (framerate === undefined) framerate = 30;
    const _interval = 1000 / framerate;
    let _remainingTime = 0;
    let _elapsedTime = 0;
    return function(dt) {
        _remainingTime -= dt;
        _elapsedTime += dt;
        if (_remainingTime <= 0) {
            callback(_elapsedTime);
            _elapsedTime = 0;
            _remainingTime = _interval + (_remainingTime % _interval);
        }
    };
}
